import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Zap } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { IMAGES } from "../content";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Chip = ({ className, delay, children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    className={`lr-chip absolute flex items-center gap-2.5 rounded-2xl border border-black/5 bg-white/80 backdrop-blur-xl px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.06)] ${className}`}
  >
    {children}
  </motion.div>
);

const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 5]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-28 md:pt-32 pb-16">
      <div className="absolute inset-0 bg-grid bg-grid-fade" aria-hidden="true" />
      <div className="absolute -top-32 right-[-10%] w-[42rem] h-[42rem] rounded-full bg-[#00E5FF]/20 blur-[130px]" aria-hidden="true" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[36rem] h-[36rem] rounded-full bg-[#14D8A5]/20 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        <div className="lg:col-span-7 pt-6 lg:pt-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#14D8A5] opacity-60 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#14D8A5]" />
            </span>
            <span className="label-mono text-[#52525B]">LendRobot — Micro advances for small bills</span>
          </motion.div>

          <h1 className="font-display font-black tracking-[-0.03em] leading-[0.95] text-[#0A1628] text-[clamp(2.6rem,6.4vw,5.8rem)]">
            <Line delay={0.15}>
              <span className="relative inline-block text-[#007AFF]">
                Micro advances,
                <motion.svg
                  className="absolute -bottom-2 left-0 w-full h-3"
                  viewBox="0 0 200 14"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M4 10 Q 100 0 196 10"
                    stroke="#14D8A5"
                    strokeWidth="6"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1.05, duration: 0.7, ease: "easeOut" }}
                  />
                </motion.svg>
              </span>
            </Line>
            <Line delay={0.28}>the minute</Line>
            <Line delay={0.41}>you need them.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-md text-base md:text-lg leading-relaxed text-[#52525B]"
          >
            When a small bill can't wait — rent, payroll, a supplier that won't. Upload your last
            4 months of business bank statements and Robo-B7 auto-approves up to{" "}
            <strong className="text-[#0A1628]">$5,000</strong> on the spot.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-bridge-btn"
              onClick={() => navigate("/apply", { state: { bridge: true, amount: 5000 } })}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#007AFF] px-7 py-4 text-base font-semibold text-white shadow-[0_12px_32px_rgba(0,122,255,0.35)] transition-all duration-300 hover:bg-[#0A1628] hover:-translate-y-0.5"
            >
              Get up to $5,000 now
              <Zap className="w-5 h-5 transition-transform duration-300 group-hover:scale-125" />
            </button>
            <button
              data-testid="hero-how-btn"
              onClick={() => scrollToId("how")}
              className="inline-flex items-center gap-2.5 rounded-full border border-black/10 bg-white/60 backdrop-blur px-7 py-4 text-base font-semibold text-[#0A1628] transition-all duration-300 hover:border-black/25 hover:-translate-y-0.5"
            >
              <Play className="w-4 h-4 text-[#007AFF]" /> See how it works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-14 flex flex-wrap gap-x-12 gap-y-5"
          >
            {[
              ["$5,000", "max micro advance"],
              ["60 sec", "auto-approval"],
              ["50 days", "at 1% per day"],
            ].map(([big, small], i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <p className="font-display text-3xl font-bold text-[#0A1628]">{big}</p>
                <p className="label-mono mt-1 text-[#52525B]">{small}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5 mt-4 lg:mt-0">
          <motion.div style={{ y, rotate }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.82, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute inset-8 rounded-full bg-gradient-to-tr from-[#00E5FF]/40 via-[#4DA3FF]/30 to-[#14D8A5]/40 blur-3xl" aria-hidden="true" />
              <img
                data-testid="hero-robot-img"
                src={IMAGES.hero}
                alt="The LendRobot mascot"
                className="lr-float relative w-full max-w-[520px] mx-auto"
                draggable="false"
              />
            </motion.div>

            <Chip testid="hero-chip-decision" delay={1.0} className="top-[16%] -left-2 md:-left-8">
              <span className="h-2.5 w-2.5 rounded-full bg-[#14D8A5]" />
              <div>
                <p className="text-sm font-semibold text-[#0A1628]">Auto-approved in 60 seconds</p>
                <p className="label-mono text-[#52525B] mt-0.5">Robo-B7 · online</p>
              </div>
            </Chip>

            <Chip testid="hero-chip-fees" delay={1.25} className="bottom-[14%] right-0 md:-right-4" >
              <span className="font-display text-2xl font-bold text-[#007AFF]">$150/day</span>
              <div>
                <p className="text-sm font-semibold text-[#0A1628]">flat, for 50 days</p>
                <p className="label-mono text-[#52525B] mt-0.5">covers small bills — same-day ACH</p>
              </div>
            </Chip>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-5 md:left-10 flex items-center gap-3"
      >
        <span className="label-mono text-[#52525B]">Scroll</span>
        <span className="block h-10 w-px bg-gradient-to-b from-[#0A1628]/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
